// =============================================================
// feed.ts — 참여 피드: 온체인 Swapped 이벤트를 읽어 "수호자 합류" 스트림 생성
//  · 지갑 연결과 무관하게 독립 RPC(JsonRpcProvider)로 동작 (방문 즉시 표시)
//  · 과거 기록 N건 로드 + 실시간 신규 이벤트 구독
//  · 서버 불필요 — 블록체인이 곧 데이터 소스
// =============================================================

import { Contract, JsonRpcProvider, formatEther } from 'ethers';
import { CHAIN, PV_GATE_ADDRESS, PV_GATE_ABI } from './config';
import { hasAddress } from './chain';

export interface FeedItem {
  buyer: string;        // 구매자 주소
  short: string;        // 0x6942…a0ef
  molOut: bigint;       // 받은 MOL (wei)
  molWhole: string;     // "20,000,000"
  txHash: string;
  blockNumber: number;
  ts: number;           // 로컬 수신 시각(ms) — 정렬/중복방지용
}

function shortAddr(a: string): string {
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}
function molWholeStr(wei: bigint): string {
  // 18 decimals → 정수부만, 천단위 콤마
  const whole = wei / 10n ** 18n;
  return whole.toLocaleString('en-US');
}

export class ParticipationFeed {
  private provider: JsonRpcProvider;
  private contract: Contract | null = null;
  private seen = new Set<string>(); // txHash 중복 방지

  constructor() {
    this.provider = new JsonRpcProvider(CHAIN.rpc, CHAIN.id);
  }

  private gate(): Contract | null {
    if (!hasAddress(PV_GATE_ADDRESS)) return null;
    if (!this.contract) {
      this.contract = new Contract(PV_GATE_ADDRESS, PV_GATE_ABI, this.provider);
    }
    return this.contract;
  }

  /** 최근 참여 N건 (과거 로그). 실패해도 빈 배열 반환(띠는 데모로 대체 가능). */
  async recent(limit = 20, lookbackBlocks = 500000): Promise<FeedItem[]> {
    const gate = this.gate();
    if (!gate) return [];
    try {
      const latest = await this.provider.getBlockNumber();
      const from = Math.max(0, latest - lookbackBlocks);
      const filter = gate.filters.Swapped();
      const logs = await gate.queryFilter(filter, from, 'latest');
      const items: FeedItem[] = [];
      for (const log of logs) {
        const item = this.toItem(log);
        if (item && !this.seen.has(item.txHash)) {
          this.seen.add(item.txHash);
          items.push(item);
        }
      }
      // 최신순 → limit
      items.sort((a, b) => b.blockNumber - a.blockNumber);
      return items.slice(0, limit);
    } catch {
      return [];
    }
  }

  /**
   * 실시간 신규 참여 구독. 새 Swapped 마다 cb 호출.
   * 반환: 구독 해제 함수.
   */
  subscribe(cb: (item: FeedItem) => void): () => void {
    const gate = this.gate();
    if (!gate) return () => {};
    const handler = (...args: unknown[]) => {
      // 마지막 인자가 이벤트 객체(log)
      const ev = args[args.length - 1] as { log?: unknown };
      const log = (ev?.log ?? ev) as Parameters<ParticipationFeed['toItem']>[0];
      const item = this.toItem(log);
      if (item && !this.seen.has(item.txHash)) {
        this.seen.add(item.txHash);
        cb(item);
      }
    };
    void gate.on('Swapped', handler);
    return () => {
      try {
        void gate.off('Swapped', handler);
      } catch {
        /* noop */
      }
    };
  }

  private toItem(log: {
    args?: Record<string, unknown> & { buyer?: unknown; molOut?: unknown };
    transactionHash?: string;
    blockNumber?: number;
  } | null): FeedItem | null {
    if (!log || !log.args) return null;
    const buyer = String(log.args.buyer ?? '');
    const molOut = (log.args.molOut as bigint) ?? 0n;
    if (!buyer) return null;
    return {
      buyer,
      short: shortAddr(buyer),
      molOut,
      molWhole: molWholeStr(molOut),
      txHash: log.transactionHash ?? buyer + ':' + String(log.blockNumber ?? 0),
      blockNumber: log.blockNumber ?? 0,
      ts: Date.now(),
    };
  }

  // formatEther 재노출(필요 시)
  static fmtBnb(wei: bigint): string {
    return Number(formatEther(wei)).toLocaleString('en-US', { maximumFractionDigits: 4 });
  }
}
